import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Box, Table } from "@mantine/core"

import {
  EmptyMessage,
  Loader,
  Pagination,
} from "components";
import { ServiceConstants } from "constant";
import { useListHook } from "hooks";
import { UserRecommendationResponse } from "types";
import { AppUtils } from "utils";

import { IRootState } from "config/redux";
import UserRecommendationListItem from "./item";
import { useDebouncedState } from "@mantine/hooks";
import { tableClasses } from "styles";

const { REQUEST_PENDING, REQUEST_USER_RECOMMENDATION_LIST_ID } = ServiceConstants;

interface UserRecommendationListProps {
  fetch: (page: number, search: string | undefined) => void;
}
const UserRecommendationList = ({ fetch }: UserRecommendationListProps) => {
  const { t } = useTranslation();
  const serviceObject = useSelector((state: IRootState) => state.service.object[REQUEST_USER_RECOMMENDATION_LIST_ID]);
  const { page, content, updatePageable, resetList, totalPages, refreshList, setPage } = useListHook();
  const [search, ] = useDebouncedState('', 500);

  useEffect(() => {
    resetList();
  }, []);

  useEffect(() => {
    AppUtils.resolveServiceObject(serviceObject, REQUEST_USER_RECOMMENDATION_LIST_ID, updatePageable);
  }, [serviceObject]);

  useEffect(() => {
    if (page === undefined) {
      return;
    }
    if (page === -1) {
      resetList();
      return;
    }
    fetch(page, search);
  }, [page]);

  useEffect(() => {
    if (search === undefined) {
      return;
    }
    refreshList();
  }, [search]);

  const isLoading = useCallback(() => serviceObject && serviceObject.status === REQUEST_PENDING, [serviceObject]);

  const rows = useMemo(() => {
    if (!content) {
      return <></>;
    }
    return content.map((el: UserRecommendationResponse) => <UserRecommendationListItem item={el} t={t} />);
  }, [content])

  return (
    <Box pos={'relative'}>
      <Loader overlay={true} visible={content === undefined || isLoading()} />
      {content && (
        content.length === 0 ?
          <EmptyMessage message={t('user.recommendation.list.empty')} />
        :
        <>
          <Table className={tableClasses.table}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('user.fullName.label')}</Table.Th>
                <Table.Th>{t('user.phone.label')}</Table.Th>
                <Table.Th visibleFrom={'md'}>{t('user.recommended.by')}</Table.Th>
                <Table.Th>{t('user.recommended.at')}</Table.Th>
                <Table.Th visibleFrom={'md'} ta={'center'}>{t('user.crm')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
          <Pagination value={page} onChange={setPage} total={totalPages} />
        </>
      )}
    </Box>
  )
}

export default UserRecommendationList;